import { useBoolean, useMap } from 'ahooks';
import { useEffect, useState } from 'react';

export const useLocalChanges = (
  selectedId: string,
  initialData: Record<string, Record<string, any>>
) => {
  const [
    localChangesMap,
    {
      set: setLocalChanges,
      remove: removeLocalChanges,
      reset: resetLocalChanges,
      get: getLocalChanges,
    },
  ] = useMap<string, Map<string, any>>();
  const [labelCounts, setLabelCounts] = useState('');
  const [isLoading, { setTrue: startLoading, setFalse: finishLoading }] =
    useBoolean(false);
  const hasUnsavedChanges = localChangesMap.size;

  useEffect(() => {
    if (isLoading) {
      finishLoading();
      setLabelCounts('Reset is active');
    }
  }, [isLoading]);

  const getInitValue = (
    key: string,
    subKey: string,
    data?: Record<string, Record<string, any>>
  ) => {
    const dataToCheck = data ?? initialData;
    const initDataByKey = dataToCheck[key];
    if (initDataByKey) {
      return initDataByKey[subKey];
    }
    return undefined;
  };

  const getSubValue = (subKey: string) =>
    getLocalChanges(selectedId)?.get(subKey) ??
    getInitValue(selectedId, subKey);

  const handleSaveData = (
    subKey: string,
    value: any,
    setterCallback?: (value: any) => void,
    mainKey?: string
  ) => {
    const selectedKey = mainKey ?? selectedId;
    if (setterCallback) {
      setterCallback(value);
    }
    if (!subKey) {
      return;
    }
    const subMap = localChangesMap.get(selectedKey) ?? new Map();
    if (value === getInitValue(selectedKey, subKey)) {
      subMap.delete(subKey);
      if (subMap.size === 0) {
        removeLocalChanges(selectedKey);
      }
      return;
    }
    subMap?.set(subKey, value);
    if (subMap) {
      setLabelCounts('');
      setLocalChanges(selectedKey, subMap);
    }
  };

  const performReset = async (
    onReset: () => Record<string, Record<string, any>>
  ) => {
    if (!onReset) {
      return;
    }
    const res = onReset();
    Object.keys(res).forEach((key) => {
      const subKeys = res[key];
      Object.keys(subKeys).forEach((subKey) => {
        handleSaveData(subKey, getInitValue(key, subKey, res), undefined, key);
      });
    });
    startLoading();
  };

  const resetChanges = () => {
    finishLoading();
    setLabelCounts('');
    resetLocalChanges();
  };
  return {
    localChangesMap,
    handleSaveData,
    getSubValue,
    performReset,
    labelCounts,
    isLoading,
    resetChanges,
    hasUnsavedChanges,
  };
};
